import { put, call, fork } from 'redux-saga/effects';
import * as actionsNO05 from '../actions/indexNO05';
import * as APICalls from '../../felles/APICalls_NO05';
import NO05, { NO05StatusStat } from '../../models/NO05';
import { KONTANT_PAYMENT_METHODS, DIREKTE_ORDER_TYPES, isPlukkOrdre, hasBkCode, isReturnToSupplierBk } from '../../pages/NO05/no05StatusGroups';

function getErrorMessage(error: any, fallback: string): string {
    const backendMessage = error?.response?.data?.error;
    return typeof backendMessage === 'string' && backendMessage.trim() ? backendMessage : fallback;
}

function mapToNO05(item: any): NO05 {
    const lines: any[] = item.products ?? [];
    const antBestilt = lines.reduce((s: number, l: any) => s + (l.quantityOrdered ?? 0), 0);
    const antLevert = lines.reduce((s: number, l: any) => s + (l.quantityDelivered ?? 0), 0);

    // Retur orders (order number starting with '9') are registered with a positive amount in the
    // backend, but represent a credit — force the sign negative here so the row and the status
    // card totals (computeStats below sums `belop`, incl. the "Kredit" card and "Alle ordre") read
    // as a minus instead of adding the return on top of the total.
    const isRetur = (item.orderNumber ?? '').toString().startsWith('9');
    const rawTotalPrice = item.totalPrice ?? 0;
    const belop = isRetur ? -Math.abs(rawTotalPrice) : rawTotalPrice;

    return new NO05(
        item.orderNumber ?? '',
        item.customerNumber ?? '',
        item.customerName ?? '',
        item.requisitionNumber ?? '',
        item.orderDate ?? '',
        item.orderTime ?? '',
        item.receiver ?? '',
        item.status ?? 'UFULLSTENDIG INNLAGT',
        antBestilt, antLevert,
        belop,
        item.grossProfit ?? 0,
        // Already negated by the backend for credit orders, unlike `belop` above — don't flip
        // the sign again here.
        item.grossProfitRevenue ?? 0,
        item.grossProfitCost ?? 0,
        item.invoiceCreditNumber ?? '',
        item.shippingNumber ?? '',
        String(item.orderType ?? ''),
        item.orderPaymentMethod ?? '',
        item.shippingMethod ?? '',
        item.speditor ?? '',
        String(item.directOrderFlag ?? ''),
        String(item.skaff ?? ''),
        String(item.bk ?? ''),
        String(item.recordType ?? ''),
    );
}

const PREFERRED_STATUS_ORDER = [
    'FERDIGMELDT.', 'FMELD. FAKT. KS-OPPGJ.', 'FERDIG PLUKKET', 'PRINTET.', 'PAKKET.',
    'FERDIG REGISTRERT.', 'VENTER GODKJENNING', 'PÅBEGYNT,IKKE FULLFØRT',
    'REG. KRED-STOPP IKKE P', 'LAGEROVF. (F.MELDT)', 'VERKSTEDSORD.(F.MELDT)',
    'UTGÅRMELDT.', 'KORR. ETTER F.MELDING.', 'RESTREG. (F.MELDT)', 'RET. TIL LEV.(F.MELDT)',
    'DELFERDIGMELDT.', 'PÅBEGYNT SAMLEORDRE', 'SLUPPET FORHÅNDSORDRE', 'UFULLSTENDIG INNLAGT',
];

// Combined BRF% the way ORVI computes it (CICSORVI.CBL BEREGN-SUM, line 1062): total
// dekningsbidrag in kroner over total dekningsgrunnlag in kroner.
//
//     BRF% = (Σ brfOmsetning − Σ brfKost) / Σ brfOmsetning × 100
//
// Not an average of the per-order BRF%-column: weighting those percentages by `belop` looks like
// another way to get there but isn't — `belop` includes surcharge and Frakt, which brfOmsetning
// excludes (decided 2026-09-21: neither ever counts towards dekningsgrad), and `belop` is rounded
// per line total rather than per unit (see brfOmsetning's backend comment), so it carries each
// order's truncation error into the total. Summing kroner avoids both.
function brfProsentOf(orders: NO05[]): number {
    const omsetning = orders.reduce((s, o) => s + o.brfOmsetning, 0);
    if (omsetning === 0) return 0;
    const kost = orders.reduce((s, o) => s + o.brfKost, 0);
    return ((omsetning - kost) / omsetning) * 100;
}

const sumBrf = (orders: NO05[]) => ({
    brfOmsetning: orders.reduce((s, o) => s + o.brfOmsetning, 0),
    brfKost:      orders.reduce((s, o) => s + o.brfKost, 0),
    brfProsent:   brfProsentOf(orders),
});

function computeStats(orders: NO05[]): NO05StatusStat[] {
    type Acc = { count: number; totalAmount: number; brfOmsetning: number; brfKost: number; antBestilt: number; antLevert: number };

    // Orders with BK='B' (retur til leverandør) are not sales — they get their own cross-status
    // box below and are excluded from every other box (Alle ordre included), so the "Total sum"
    // matches ORVI instead of being inflated by supplier returns.
    const returnToSupplier = orders.filter((o) => isReturnToSupplierBk(o.bk));

    // ORVI's "status U excluded" rule (CICSORVI.CBL:2841) only fires when the operator's
    // ORDRETYPE search parameter (COM-ORDTYPE, a whole-search-session value the operator types
    // into ORVI's mandatory ORDRETYPE box — NOT a per-order field, despite the coincidental
    // overlap with ordre_nrf's own per-order O-ORDRETYPE/OrderTypeFlag letters) is literally 'D'.
    // NO05's "alle" search corresponds to ORVI run with ORDRETYPE='S' (COM-ORDTYPE='S' sets
    // COM-SYNKE='J' — descending sort, no status/date restriction — CICSORVI.CBL:1769,3156), so
    // this exclusion never applies here and orders don't get filtered by status at all.
    const coreOrders = orders.filter((o) => !isReturnToSupplierBk(o.bk));

    const returs = coreOrders.filter((o) => o.ordreNr.startsWith('9'));

    const groups: Record<string, Acc> = {};
    for (const o of coreOrders) {
        const key = o.status;
        if (!groups[key]) groups[key] = { count: 0, totalAmount: 0, brfOmsetning: 0, brfKost: 0, antBestilt: 0, antLevert: 0 };
        groups[key].count++;
        groups[key].totalAmount  += o.belop;
        groups[key].brfOmsetning += o.brfOmsetning;
        groups[key].brfKost      += o.brfKost;
        groups[key].antBestilt   += o.antBestilt;
        groups[key].antLevert    += o.antLevert;
    }

    const toStat = (status: string, g: Acc): NO05StatusStat => ({
        status,
        count:        g.count,
        totalAmount:  g.totalAmount,
        brfOmsetning: g.brfOmsetning,
        brfKost:      g.brfKost,
        brfProsent:   g.brfOmsetning !== 0 ? ((g.brfOmsetning - g.brfKost) / g.brfOmsetning) * 100 : 0,
        antBestilt:   g.antBestilt,
        antLevert:    g.antLevert,
    });

    const allStat: NO05StatusStat = {
        status:      'Alle ordre',
        count:       coreOrders.length,
        totalAmount: coreOrders.reduce((s, o) => s + o.belop, 0),
        ...sumBrf(coreOrders),
        antBestilt:  coreOrders.reduce((s, o) => s + o.antBestilt, 0),
        antLevert:   coreOrders.reduce((s, o) => s + o.antLevert, 0),
    };

    const stats: NO05StatusStat[] = [allStat];

    for (const name of PREFERRED_STATUS_ORDER) {
        if (groups[name]) {
            stats.push(toStat(name, groups[name]));
            delete groups[name];
        }
    }
    for (const [name, g] of Object.entries(groups)) {
        stats.push(toStat(name, g));
    }

    if (returs.length > 0) {
        stats.push({
            status:      'Kredit',
            count:       returs.length,
            totalAmount: returs.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(returs),
            antBestilt:  returs.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   returs.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const kontant = coreOrders.filter((o) => KONTANT_PAYMENT_METHODS.includes(o.betalingsKode));
    if (kontant.length > 0) {
        stats.push({
            status:      'Kontant',
            count:       kontant.length,
            totalAmount: kontant.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(kontant),
            antBestilt:  kontant.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   kontant.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const direkte = coreOrders.filter((o) => DIREKTE_ORDER_TYPES.includes(o.direkteFlagg));
    if (direkte.length > 0) {
        stats.push({
            status:      'Direkte ordre',
            count:       direkte.length,
            totalAmount: direkte.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(direkte),
            antBestilt:  direkte.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   direkte.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const plukk = coreOrders.filter((o) => isPlukkOrdre(o.skaff));
    if (plukk.length > 0) {
        stats.push({
            status:      'Plukkordre',
            count:       plukk.length,
            totalAmount: plukk.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(plukk),
            antBestilt:  plukk.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   plukk.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const bkCode = coreOrders.filter((o) => hasBkCode(o.bk));
    if (bkCode.length > 0) {
        stats.push({
            status: 'Ordre med BK kode',
            count:       bkCode.length,
            totalAmount: bkCode.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(bkCode),
            antBestilt:  bkCode.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   bkCode.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    if (returnToSupplier.length > 0) {
        stats.push({
            status: 'Retur til leverandør',
            count:       returnToSupplier.length,
            totalAmount: returnToSupplier.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(returnToSupplier),
            antBestilt:  returnToSupplier.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   returnToSupplier.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    return stats;
}

// Initial page load, split into two independent, concurrent fetches instead of one combined
// call — the table no longer has to wait on the slow full/unpaged fetch that the status cards
// need for accurate counts:
//  - quickInitialTableLoad: one fast, small page → table renders almost immediately.
//  - fullInitialStatsLoad: the full open-order set (unchanged from before) → status cards
//    (hidden until this arrives, same as today), and once it lands it also replaces the
//    table's quick page with the complete set so status-card filtering has everything to
//    filter against. Forked so a rapid second search (takeLatest) cancels both cleanly.
function* quickInitialTableLoad(companyId: string) {
  try {
    const response: any = yield call(APICalls.searchOpenOrders, { companyId, page: 0, pageSize: 30 });
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      // hasMore here just controls scroll-triggered paging until the full fetch below lands
      // and replaces this quick page outright (see fullInitialStatsLoad).
      yield put(actionsNO05.NO05SearchSuccess(raw.map(mapToNO05), raw.length >= 30));
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05SearchFailure(getErrorMessage(error, 'Kunne ikke hente åpne ordre. Prøv igjen.')));
  }
}

function* fullInitialStatsLoad(companyId: string) {
  try {
    yield put(actionsNO05.NO05StatsRequest(companyId));
    const response: any = yield call(APICalls.loadOpenOrdersStats, companyId);
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      const orders: NO05[] = raw.map(mapToNO05);
      yield put(actionsNO05.NO05StatsSuccess(computeStats(orders)));
      yield put(actionsNO05.NO05SearchSuccess(orders, false));
    }
  } catch (error: any) {
    // Only the stats/cards side fails here — the table already has the quick page from
    // quickInitialTableLoad (a genuinely separate, unrelated request), and NO05SearchFailure
    // would wipe that out (the reducer clears `orders` on that action). Leave it standing.
    yield put(actionsNO05.NO05StatsFailure(getErrorMessage(error, 'Kunne ikke hente statuskort. Prøv igjen.')));
  }
}

export function* searchNO05OrdersSaga(action: any) {
  try {
    const isInitialLoad = action.payload.skipPaging === true;

    if (isInitialLoad) {
      yield fork(quickInitialTableLoad, action.payload.companyId);
      yield fork(fullInitialStatsLoad, action.payload.companyId);
    } else {
      // Filtered search — stats reflect the search results
      const response: any = yield call(APICalls.searchOpenOrders, { ...action.payload, page: 0, pageSize: 100 });
      if (response?.data) {
        const raw: any[] = response.data.orders ?? response.data.content ?? [];
        const orders: NO05[] = raw.map(mapToNO05);
        yield put(actionsNO05.NO05StatsSuccess(computeStats(orders)));
        yield put(actionsNO05.NO05SearchSuccess(orders, raw.length >= 100));
      }
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05SearchFailure(getErrorMessage(error, 'Kunne ikke søke etter ordre. Prøv igjen.')));
  }
}

export function* loadMoreNO05OrdersSaga(action: any) {
  try {
    // Strip skipPaging — load-more always uses regular paging
    const { skipPaging: _, ...pagedParams } = action.payload;
    const response = yield call(APICalls.searchOpenOrders, pagedParams);
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      const newOrders: NO05[] = raw.map(mapToNO05);
      const hasMore = raw.length >= (pagedParams.pageSize ?? 30);

      yield put(actionsNO05.NO05LoadMoreSuccess(newOrders, hasMore));
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05LoadMoreFailure(getErrorMessage(error, 'Kunne ikke laste flere ordre. Prøv igjen.')));
  }
}

export function* fetchNO05OrderDetailSaga(action: any) {
  try {
    const { companyId, orderNumber } = action.payload;
    const response = yield call(APICalls.fetchOrderDetail, companyId, orderNumber);
    yield put(actionsNO05.NO05OrderDetailSuccess(response.data));
  } catch (error: any) {
    yield put(actionsNO05.NO05OrderDetailFailure(getErrorMessage(error, 'Kunne ikke hente ordredetaljer. Prøv igjen.')));
  }
}
